<template>
  <button
    :class="buttonClasses"
    :disabled="disabled"
    :aria-label="props.ariaLabel"
    :aria-describedby="props.ariaDescribedby"
    @click="handleClick"
  >
    <BaseIcon
      :size="iconSize"
      :variant="computedIconVariant"
      :class="iconClasses"
      :aria-label="props.iconAriaLabel"
    >
      <slot />
    </BaseIcon>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseIcon from '@/components/ui/BaseIcon.vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'purple' | 'ghost' | 'blue'
  size?: 'small' | 'regular' | 'large' | 'xs'
  iconSize?: 'small' | 'regular' | 'large'
  iconVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'purple' | 'ghost' | 'blue'
  disabled?: boolean
  ariaLabel?: string
  ariaDescribedby?: string
  iconAriaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary' as const,
  size: 'regular' as const,
  iconSize: 'regular' as const,
  iconVariant: undefined, // Will be computed based on button variant
  disabled: false
})

// Compute icon variant based on button variant if not explicitly set
const computedIconVariant = computed(() => {
  // If iconVariant is explicitly provided, use it
  if (props.iconVariant !== undefined) {
    return props.iconVariant
  }
  
  // For ghost buttons, use ghost variant for icon to match text color
  if (props.variant === 'ghost') {
    return 'ghost'
  }
  
  // For semantic buttons, use ghost variant so icon inherits foreground color
  const foregroundVariants = ['primary', 'success', 'warning', 'danger', 'purple']
  if (foregroundVariants.includes(props.variant)) {
    return 'ghost'
  }
  
  // Default to ghost for secondary as well
  return 'ghost'
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const handleClick = (event: MouseEvent) => {
  if (!props.disabled) {
    emit('click', event)
  }
}

// Map size to button classes
const sizeClasses = computed(() => {
  const sizeMap: Record<string, string> = {
    xs: 'p-1',
    small: 'p-2',
    regular: 'p-3',
    large: 'p-4'
  }
  return sizeMap[props.size]
})

// Map variant to button classes
const variantClasses = computed(() => {
  const variantMap: Record<string, string> = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
    success: 'bg-success text-success-foreground hover:bg-success-hover',
    warning: 'bg-warning text-warning-foreground hover:bg-warning-hover',
    danger: 'bg-danger text-danger-foreground hover:bg-danger-hover',
    purple: 'bg-purple text-purple-foreground hover:bg-purple-hover',
    ghost: 'bg-transparent text-text hover:bg-surface-muted',
    blue: 'bg-transparent hover:bg-badge-blue text-badge-blue'
  }
  return variantMap[props.variant]
})

// Icon classes - ensure icons in semantic buttons are white
const iconClasses = computed(() => {
  const foregroundVariants = ['primary', 'success', 'warning', 'danger', 'purple']
  
  // For semantic buttons, make icon use foreground color (white) to match button text
  if (foregroundVariants.includes(props.variant) && props.iconVariant === undefined) {
    const foregroundColorMap = {
      primary: '!text-primary-foreground',
      success: '!text-success-foreground',
      warning: '!text-warning-foreground',
      danger: '!text-danger-foreground',
      purple: '!text-purple-foreground'
    }
    return foregroundColorMap[props.variant as keyof typeof foregroundColorMap]
  }
  
  return ''
})

// Combine all classes
const buttonClasses = computed(() => {
  return [
    'inline-flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2',
    sizeClasses.value,
    variantClasses.value,
    props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  ].join(' ')
})
</script>
